import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, Upload, X, BarChart3, Wallet, FileText, Download, Check, Ban, Users, ShoppingBag, DollarSign, Crown, Mail, Megaphone } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth } from "../contexts";
import { confirmDialog } from "../components/ConfirmModal";
import { StreamLinksFields } from "../components/StreamLinks";
import { PromoKitModal } from "../components/PromoKit";
import { toast } from "sonner";

const TABS = [
  { key: "analytics", label: "Analytics" },
  { key: "payouts", label: "Payouts" },
  { key: "reports", label: "Reports" },
  { key: "mixtapes", label: "Mixtapes" },
  { key: "singles", label: "Singles" },
  { key: "videos", label: "Videos" },
  { key: "books", label: "Books" },
  { key: "products", label: "Products" },
  { key: "banners", label: "Banners" },
  { key: "users", label: "Users" },
  { key: "orders", label: "Orders" },
  { key: "moderation", label: "Moderation" },
  { key: "subscribers", label: "Subscribers" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("analytics");

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
        <p className="font-mono-label text-neutral-500">Admin only.</p>
      </div>
    );
  }

  return (
    <div className="pb-40">
      <div className="border-b border-neutral-800 bg-[var(--surface)]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">CONTROL ROOM</div>
          <h1 className="font-display text-5xl uppercase leading-none">Admin Dashboard</h1>
        </div>
      </div>
      <div className="border-b border-neutral-800 sticky top-[68px] bg-black/95 backdrop-blur-xl z-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`font-mono-label text-[11px] px-4 py-4 border-b-2 transition-colors ${tab===t.key ? "border-[var(--accent)] text-white" : "border-transparent text-neutral-400 hover:text-white"}`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10">
        {tab === "analytics" && <AnalyticsPanel />}
        {tab === "payouts" && <PayoutsPanel />}
        {tab === "reports" && <ReportsPanel />}
        {["mixtapes","singles","videos","books"].includes(tab) && <ContentCRUD ctype={tab} />}
        {tab === "products" && <ProductCRUD />}
        {tab === "banners" && <BannerCRUD />}
        {tab === "users" && <UsersList />}
        {tab === "orders" && <OrdersList />}
        {tab === "moderation" && <ModerationPanel />}
        {tab === "subscribers" && <SubscribersPanel />}
      </div>
    </div>
  );
}

// ─── FILE UPLOAD ──────────────────────────────────────────────────────────────
function FileUploadField({ label, kind, value, onChange, accept }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="font-mono-label text-[10px] text-neutral-400 mb-2">{label}</div>
      <div className="flex gap-3 items-start">
        <label className="btn-ghost cursor-pointer" data-testid={`upload-${kind}-${label.replace(/\s+/g, "-").toLowerCase()}`}>
          <Upload size={14} /> {busy ? "Uploading…" : `Upload ${kind}`}
          <input type="file" accept={accept} onChange={handle} className="hidden" />
        </label>
        {value && (
          <div className="flex items-center gap-2 text-[10px] font-mono-label text-neutral-400 break-all max-w-md">
            {kind === "image" ? <img src={resolveFileUrl(value)} alt="" className="w-10 h-10 object-cover border border-neutral-800" /> : null}
            <span>{value.split("/").pop()}</span>
            <button onClick={() => onChange("")} className="text-[var(--accent)]"><X size={12} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CONTENT (mixtapes / singles / videos / books) ────────────────────────────
function ContentCRUD({ ctype }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [promo, setPromo] = useState(null);
  const canPromo = ctype !== "books";

  const load = () => api.get(`/content/${ctype}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [ctype]);

  const remove = async (id, title) => {
    const ok = await confirmDialog({
      title: `Delete ${ctype.slice(0, -1)}?`,
      message: `"${title || "This item"}" will be permanently removed from the catalog. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/content/${ctype}/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed. Please try again.");
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="font-headline text-2xl uppercase">{ctype}</h2>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid={`admin-new-${ctype}`}>
          <Plus size={14} /> New {ctype.slice(0, -1)}
        </button>
      </div>
      <div className="border border-neutral-800" data-testid={`admin-${ctype}-list`}>
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-3 border-b border-neutral-800 last:border-0">
            {it.cover_image && <img src={resolveFileUrl(it.cover_image)} alt="" className="w-12 h-12 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-headline uppercase truncate">{it.title}</div>
              <div className="font-mono-label text-[10px] text-neutral-400">{it.artist} · {it.genre} · {it.year}</div>
            </div>
            {canPromo && (
              <button onClick={() => setPromo(it)} className="font-mono-label text-[10px] px-3 py-2 border border-neutral-800 hover:border-[var(--accent)] hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid={`promo-kit-${it.id}`}>
                <Megaphone size={12} /> PROMO KIT
              </button>
            )}
            <button onClick={() => setEditing(it)} className="text-neutral-300 hover:text-white p-2" data-testid={`edit-${it.id}`}><Edit3 size={14} /></button>
            <button onClick={() => remove(it.id, it.title)} className="text-neutral-300 hover:text-[var(--accent)] p-2" data-testid={`delete-${it.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center font-mono-label text-neutral-500">No items yet.</div>}
      </div>
      {editing && (
        <ContentForm
          ctype={ctype}
          initial={editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
      {promo && <PromoKitModal ctype={ctype} item={promo} onClose={() => setPromo(null)} />}
    </div>
  );
}

function ContentForm({ ctype, initial, onClose }) {
  const [f, setF] = useState({
    title: "", artist: "", description: "", cover_image: "", file_url: "",
    external_url: "", genre: "Hip-Hop", year: new Date().getFullYear(),
    is_official: false, featured: false, is_indy: false, early_access: false, tracklist: [], duration: "", price: 0,
    tracks: [], stream_links: {},
    ...initial,
  });
  const [busy, setBusy] = useState(false);
  const [uploadingTracks, setUploadingTracks] = useState(false);
  const tracksInput = React.useRef(null);
  const isEdit = !!initial?.id;
  const isAudio = ctype === "mixtapes" || ctype === "singles";
  const isVideo = ctype === "videos";
  const isBook  = ctype === "books";
  const isMixtape = ctype === "mixtapes";

  const uploadTracks = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingTracks(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((file) => fd.append("files", file));
      const r = await api.post("/upload-tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newTracks = r.data.tracks || [];
      if (!newTracks.length) { toast.error("No playable audio found."); return; }
      setF((s) => ({ ...s, tracks: [...(s.tracks || []), ...newTracks] }));
      toast.success(`Added ${newTracks.length} track${newTracks.length > 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Track upload failed");
    } finally {
      setUploadingTracks(false);
    }
  };
  const moveTrack = (idx, dir) => setF((s) => {
    const arr = [...(s.tracks || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...s, tracks: arr };
  });
  const removeTrack = (idx) => setF((s) => ({ ...s, tracks: (s.tracks || []).filter((_, i) => i !== idx) }));
  const renameTrack = (idx, title) => setF((s) => ({ ...s, tracks: (s.tracks || []).map((t, i) => (i === idx ? { ...t, title } : t)) }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, year: f.year ? parseInt(f.year) : null, price: parseFloat(f.price || 0) };
      if (typeof body.tracklist === "string") {
        body.tracklist = body.tracklist.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      if (isEdit) await api.put(`/content/${ctype}/${initial.id}`, body);
      else await api.post(`/content/${ctype}`, body);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="content-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-2xl w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-headline text-2xl uppercase">{isEdit ? "Edit" : "New"} {ctype.slice(0, -1)}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <input className="input-brut" placeholder="Name (Artist / Author)" value={f.artist} onChange={(e) => setF({...f, artist:e.target.value})} data-testid="form-artist" />
          <input className="input-brut" placeholder="Title" value={f.title} onChange={(e) => setF({...f, title:e.target.value})} data-testid="form-title" />
          <textarea rows={3} className="input-brut resize-none" placeholder="Description" value={f.description} onChange={(e) => setF({...f, description:e.target.value})} data-testid="form-description" />
          <div className="grid sm:grid-cols-3 gap-3">
            <input className="input-brut" placeholder="Genre" value={f.genre} onChange={(e) => setF({...f, genre:e.target.value})} data-testid="form-genre" />
            <input className="input-brut" type="number" placeholder="Year" value={f.year} onChange={(e) => setF({...f, year:e.target.value})} data-testid="form-year" />
            <input className="input-brut" placeholder="Duration / Length" value={f.duration} onChange={(e) => setF({...f, duration:e.target.value})} data-testid="form-duration" />
          </div>
          <FileUploadField label="Cover Image" kind="image" accept="image/*" value={f.cover_image} onChange={(v) => setF({...f, cover_image:v})} />
          {isAudio && <FileUploadField label="Audio File" kind="audio" accept="audio/*,.mp3,.wav,.m4a,.m4b,.aac,.ogg,.oga,.flac,.aiff,.aif,.alac,.opus,.wma,.amr,.caf,.mka,.3gp,.3gpp,.webm" value={f.file_url} onChange={(v) => setF({...f, file_url:v})} />}
          {isAudio && (
            <FileUploadField
              label="Audio ZIP (multi-track / stems / media kit · optional)"
              kind="zip"
              accept=".zip,application/zip,application/x-zip-compressed"
              value={f.media_kit_url}
              onChange={(v) => setF({...f, media_kit_url:v})}
            />
          )}
          {isVideo && (
            <>
              <FileUploadField label="Video Upload" kind="video" accept="video/*" value={f.file_url} onChange={(v) => setF({...f, file_url:v})} />
              <input className="input-brut" placeholder="OR YouTube embed URL (https://www.youtube.com/embed/...)" value={f.external_url} onChange={(e) => setF({...f, external_url:e.target.value})} data-testid="form-external" />
            </>
          )}
          {isBook && <FileUploadField label="Book PDF" kind="pdf" accept="application/pdf" value={f.file_url} onChange={(v) => setF({...f, file_url:v})} />}
          {isMixtape && (
            <div className="border border-neutral-800 p-4" data-testid="admin-form-tracks-card">
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-2">
                <Upload size={11} /> MIXTAPE TRACKS · UPLOAD A .ZIP OR PICK MANY AUDIO FILES · PLAYS BACK-TO-BACK
              </div>
              <p className="font-mono-label text-[9px] text-neutral-500 mb-3">
                UPLOAD ORDER = PLAY ORDER. USE ▲ / ▼ TO REORDER, ✕ TO REMOVE.
              </p>
              <input
                ref={tracksInput}
                type="file"
                multiple
                accept=".zip,application/zip,application/x-zip-compressed,.mp3,.wav,.m4a,.aac,.ogg,.flac,.opus,.aiff,.webm,audio/*"
                className="hidden"
                onChange={(e) => { const files = e.target.files; if (files?.length) uploadTracks(files); e.target.value = ""; }}
                data-testid="admin-form-tracks-file"
              />
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => tracksInput.current?.click()}
                  disabled={uploadingTracks}
                  className="btn-ghost"
                  data-testid="admin-form-tracks-upload-btn"
                >
                  <Upload size={12} /> {uploadingTracks ? "Uploading…" : (f.tracks || []).length ? "Add More Tracks" : "Upload Tracks / ZIP"}
                </button>
                {(f.tracks || []).length > 0 && (
                  <button type="button" onClick={() => setF((s) => ({ ...s, tracks: [] }))} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid="admin-form-tracks-clear">
                    CLEAR ALL
                  </button>
                )}
                <span className="font-mono-label text-[10px] text-neutral-500">
                  {(f.tracks || []).length} TRACK{(f.tracks || []).length === 1 ? "" : "S"}
                </span>
              </div>
              {(f.tracks || []).length > 0 && (
                <ul className="border border-neutral-800" data-testid="admin-form-tracks-list">
                  {(f.tracks || []).map((t, i) => (
                    <li key={`${t.url}-${i}`} className="flex items-center gap-2 px-3 py-2 border-b border-neutral-800 last:border-0">
                      <span className="font-mono-label text-[10px] text-neutral-500 w-6">{(i + 1).toString().padStart(2, "0")}</span>
                      <input
                        className="input-brut flex-1 text-sm py-1"
                        value={t.title}
                        onChange={(e) => renameTrack(i, e.target.value)}
                        data-testid={`admin-form-track-title-${i}`}
                      />
                      <audio src={resolveFileUrl(t.url)} controls className="h-8" preload="none" />
                      <button type="button" onClick={() => moveTrack(i, -1)} disabled={i === 0} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)] disabled:opacity-30" aria-label="Move up">▲</button>
                      <button type="button" onClick={() => moveTrack(i, 1)} disabled={i === (f.tracks.length - 1)} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)] disabled:opacity-30" aria-label="Move down">▼</button>
                      <button type="button" onClick={() => removeTrack(i)} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)] hover:text-[var(--accent)]" aria-label="Remove">
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!isBook && <StreamLinksFields value={f.stream_links} onChange={(v) => setF((s) => ({ ...s, stream_links: v }))} />}
          {(ctype === "mixtapes") && (
            <textarea rows={4} className="input-brut resize-none" placeholder="Written tracklist (one per line · optional if tracks are uploaded above)" value={Array.isArray(f.tracklist) ? f.tracklist.join("\n") : f.tracklist} onChange={(e) => setF({...f, tracklist:e.target.value})} data-testid="form-tracklist" />
          )}
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 font-mono-label text-[11px]"><input type="checkbox" checked={!!f.is_official} onChange={(e) => setF({...f, is_official:e.target.checked})} data-testid="form-official" /> OFFICIAL</label>
            <label className="flex items-center gap-2 font-mono-label text-[11px]"><input type="checkbox" checked={!!f.featured} onChange={(e) => setF({...f, featured:e.target.checked})} data-testid="form-featured" /> FEATURED</label>
            <label className="flex items-center gap-2 font-mono-label text-[11px]"><input type="checkbox" checked={!!f.is_indy} onChange={(e) => setF({...f, is_indy:e.target.checked})} data-testid="form-indy" /> INDY</label>
            <label className="flex items-center gap-2 font-mono-label text-[11px]"><input type="checkbox" checked={!!f.early_access} onChange={(e) => setF({...f, early_access:e.target.checked})} data-testid="form-early-access" /> CULTURE PASS · EARLY ACCESS</label>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="form-save">{busy ? "Saving…" : "Save"}</button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PRODUCTS ─────────────────────────────────────────────────────────────────
function ProductCRUD() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id, name) => {
    const ok = await confirmDialog({ title: "Delete product?", message: `"${name || "This product"}" will be removed from the merch store.`, confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/products/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="font-headline text-2xl uppercase">Products</h2>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="admin-new-product"><Plus size={14} /> New Product</button>
      </div>
      <div className="border border-neutral-800" data-testid="admin-products-list">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-3 border-b border-neutral-800 last:border-0">
            {p.image && <img src={resolveFileUrl(p.image)} alt="" className="w-12 h-12 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-headline uppercase truncate">{p.title}</div>
              <div className="font-mono-label text-[10px] text-neutral-400">{p.category} · ${p.price} · stock {p.stock}</div>
            </div>
            <button onClick={() => setEditing(p)} className="text-neutral-300 hover:text-white p-2"><Edit3 size={14} /></button>
            <button onClick={() => remove(p.id, p.title)} className="text-neutral-300 hover:text-[var(--accent)] p-2" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center font-mono-label text-neutral-500">No products.</div>}
      </div>
      {editing && <ProductForm initial={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}
function ProductForm({ initial, onClose }) {
  const [f, setF] = useState({ title: "", description: "", price: 0, image: "", category: "merch", stock: 100, sizes: [], featured: false, ...initial });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, price: parseFloat(f.price), stock: parseInt(f.stock || 0) };
      if (typeof body.sizes === "string") body.sizes = body.sizes.split(",").map(s => s.trim()).filter(Boolean);
      if (isEdit) await api.put(`/products/${initial.id}`, body);
      else await api.post("/products", body);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-2xl w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-headline text-2xl uppercase">{isEdit ? "Edit" : "New"} Product</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <input className="input-brut" placeholder="Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} data-testid="product-form-title" />
          <textarea rows={3} className="input-brut resize-none" placeholder="Description" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} />
          <div className="grid sm:grid-cols-3 gap-3">
            <input className="input-brut" type="number" step="0.01" placeholder="Price" value={f.price} onChange={(e)=>setF({...f,price:e.target.value})} data-testid="product-form-price" />
            <input className="input-brut" type="number" placeholder="Stock" value={f.stock} onChange={(e)=>setF({...f,stock:e.target.value})} />
            <select className="input-brut" value={f.category} onChange={(e)=>setF({...f,category:e.target.value})} data-testid="product-form-category">
              <option value="merch">Merch</option>
              <option value="book">Book</option>
            </select>
          </div>
          <FileUploadField label="Product Image" kind="image" accept="image/*" value={f.image} onChange={(v)=>setF({...f,image:v})} />
          <input className="input-brut" placeholder="Sizes (comma-separated, e.g. S,M,L)" value={Array.isArray(f.sizes) ? f.sizes.join(",") : f.sizes} onChange={(e)=>setF({...f,sizes:e.target.value})} />
          <label className="flex items-center gap-2 font-mono-label text-[11px]"><input type="checkbox" checked={!!f.featured} onChange={(e)=>setF({...f,featured:e.target.checked})} /> FEATURED</label>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="product-form-save">{busy?"Saving…":"Save"}</button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── BANNERS ──────────────────────────────────────────────────────────────────
function BannerCRUD() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ title: "", subtitle: "", image: "", link: "", placement: "home_top", active: true });
  const load = () => api.get("/banners").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    await api.post("/banners", f);
    setAdding(false); setF({ title: "", subtitle: "", image: "", link: "", placement: "home_top", active: true });
    load();
  };
  const remove = async (id, title) => {
    const ok = await confirmDialog({ title: "Delete banner?", message: `"${title || "This banner"}" will stop appearing on the site.`, confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/banners/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="font-headline text-2xl uppercase">Banners</h2>
        <button onClick={() => setAdding((a) => !a)} className="btn-primary" data-testid="admin-new-banner"><Plus size={14} /> New Banner</button>
      </div>
      {adding && (
        <div className="border border-neutral-800 p-5 mb-6 space-y-3">
          <input className="input-brut" placeholder="Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} data-testid="banner-form-title" />
          <input className="input-brut" placeholder="Subtitle (optional)" value={f.subtitle || ""} onChange={(e)=>setF({...f,subtitle:e.target.value})} data-testid="banner-form-subtitle" />
          <FileUploadField label="Banner Image" kind="image" accept="image/*" value={f.image} onChange={(v)=>setF({...f,image:v})} />
          <input className="input-brut" placeholder="Link URL (optional)" value={f.link} onChange={(e)=>setF({...f,link:e.target.value})} data-testid="banner-form-link" />
          <select className="input-brut" value={f.placement} onChange={(e)=>setF({...f,placement:e.target.value})} data-testid="banner-form-placement">
            <option value="home_top">Home Top</option>
            <option value="home_mid">Home Middle</option>
            <option value="sidebar">Sidebar</option>
          </select>
          <button onClick={save} className="btn-primary" data-testid="banner-form-save">Save Banner</button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-banners-list">
        {items.map((b) => (
          <div key={b.id} className="border border-neutral-800" data-testid={`banner-item-${b.id}`}>
            <img src={resolveFileUrl(b.image)} alt="" className="w-full aspect-[16/6] object-cover" />
            <div className="p-3 flex justify-between items-center">
              <div className="min-w-0">
                <div className="font-headline uppercase truncate">{b.title}</div>
                <div className="font-mono-label text-[10px] text-neutral-400">{b.placement}</div>
              </div>
              <button onClick={() => remove(b.id, b.title)} className="text-[var(--accent)]" data-testid={`delete-banner-${b.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── USERS ────────────────────────────────────────────────────────────────────
function UsersList() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const ban = async (id, banned) => { await api.put(`/users/${id}/ban?banned=${banned}`); load(); };
  const del = async (u) => {
    const ok = await confirmDialog({
      title: `Delete ${u.display_name}?`,
      message: `This permanently removes @${u.username} and all their bookmarks, applications, jobs, portfolio items, messages, and newsletter row. Their published content (mixtapes/singles/videos/books) stays online but is marked "artist removed". This action cannot be undone.`,
      confirmLabel: "Delete Permanently",
      destructive: true,
    });
    if (!ok) return;
    try {
      const r = await api.delete(`/admin/users/${u.id}`);
      const c = r.data?.cascade || {};
      const summary = Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ") || "no cascading data";
      toast.success(`Deleted @${u.username} — ${summary}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <div>
      <h2 className="font-headline text-2xl uppercase mb-6">Users</h2>
      <div className="border border-neutral-800" data-testid="admin-users-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-4 p-3 border-b border-neutral-800 last:border-0">
            <div className="flex-1 min-w-0">
              <div className="font-headline uppercase truncate">{u.display_name} <span className="text-neutral-500 text-sm">@{u.username}</span></div>
              <div className="font-mono-label text-[10px] text-neutral-400">{u.email} · {u.role}{u.is_artist && " · ARTIST"}{u.banned && " · BANNED"}</div>
            </div>
            {u.role !== "admin" && (
              <div className="flex items-center gap-2">
                <button onClick={() => ban(u.id, !u.banned)} className={u.banned ? "btn-primary !py-2 !px-3" : "btn-ghost !py-2 !px-3"} data-testid={`ban-${u.id}`}>
                  {u.banned ? "UNBAN" : "BAN"}
                </button>
                <button
                  onClick={() => del(u)}
                  className="font-mono-label text-[10px] px-3 py-2 border border-red-600 text-red-500 hover:bg-red-600 hover:text-white transition-colors"
                  data-testid={`delete-user-${u.id}`}
                  title="Permanently delete this user"
                >
                  DELETE
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ORDERS ───────────────────────────────────────────────────────────────────
function OrdersList() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div>
      <h2 className="font-headline text-2xl uppercase mb-6">Orders</h2>
      <div className="border border-neutral-800" data-testid="admin-orders-list">
        {orders.length === 0 && <div className="p-10 text-center font-mono-label text-neutral-500">No orders yet.</div>}
        {orders.map((o) => (
          <div key={o.id} className="p-4 border-b border-neutral-800 last:border-0">
            <div className="flex justify-between items-center mb-2">
              <div className="font-mono-label text-[10px] text-neutral-400">#{o.id.slice(0,8).toUpperCase()} · {new Date(o.created_at).toLocaleString()}</div>
              <span className={`chip ${o.status==="paid" ? "chip-accent" : ""}`}>{o.status.toUpperCase()}</span>
            </div>
            <div className="font-mono-label text-[10px] text-neutral-400">{o.shipping?.name} · {o.shipping?.email} · {o.shipping?.city}, {o.shipping?.state}</div>
            <div className="flex justify-between mt-2"><span className="font-headline uppercase">Total</span><span className="font-display text-xl">${o.total.toFixed(2)}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS PANEL
// ─────────────────────────────────────────────────────────────────────────────
function AnalyticsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);
  // Memoize catalog aggregations so we don't recompute on every render.
  const catalogTotal = useMemo(
    () => Object.values(data?.content_counts || {}).reduce((a, b) => a + b, 0),
    [data?.content_counts],
  );
  const catalogBreakdown = useMemo(
    () => Object.entries(data?.content_counts || {}).map(([k, v]) => `${k}:${v}`).join(" · "),
    [data?.content_counts],
  );
  if (!data) return <p className="font-mono-label text-[10px] text-neutral-500">Loading analytics…</p>;
  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
  return (
    <div className="space-y-10" data-testid="admin-analytics">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="USERS" value={data.users} sub={`${data.last_30d?.new_users || 0} new last 30d`} icon={Users} />
        <KPI label="ARTISTS" value={data.artists} sub={`${data.culture_pass_active} Culture Pass active`} icon={Crown} accent />
        <KPI label="STREAMS" value={Number(data.plays_total).toLocaleString()} sub={`${fmt$(data.stream_payout_owed)} owed @ $${(data.stream_rate||0.005).toFixed(3)}/play`} icon={BarChart3} />
        <KPI label="TIPS PAID" value={fmt$(data.tips_total)} sub={`${data.tips_count} tips · ${data.pass_supporter_tips} pass supporters`} icon={DollarSign} accent />
        <KPI label="GMV (PAID)" value={fmt$(data.gmv)} sub={`${data.orders_paid} paid orders`} icon={ShoppingBag} />
        <KPI label="CULTURE PASS $" value={fmt$(data.culture_pass_revenue)} sub={`${data.culture_pass_txns} transactions`} icon={Crown} />
        <KPI label="PAID OUT" value={fmt$(data.paid_out_total)} sub={`${fmt$(data.pending_payout_total)} pending`} icon={Wallet} />
        <KPI label="CATALOG" value={catalogTotal} sub={catalogBreakdown} icon={FileText} />
      </div>

      <section>
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TOP ARTISTS BY TIPS (ALL-TIME)</div>
        {data.top_artists?.length ? (
          <div className="border border-neutral-800" data-testid="admin-top-artists">
            {data.top_artists.map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 p-3 border-b border-neutral-800 last:border-0">
                <span className="font-display text-xl text-neutral-500 w-8">{i + 1}</span>
                {a.avatar && <img src={resolveFileUrl(a.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />}
                <div className="flex-1 font-headline uppercase">{a.display_name}<span className="text-neutral-500 ml-2 text-xs">@{a.username}</span></div>
                <div className="font-display text-xl text-[var(--accent)]">{fmt$(a.tips_total)}</div>
              </div>
            ))}
          </div>
        ) : <EmptyHint>No tips recorded yet.</EmptyHint>}
      </section>

      <section>
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TOP CONTENT BY PLAYS</div>
        <div className="border border-neutral-800" data-testid="admin-top-content">
          {data.top_content?.map((c, i) => (
            <div key={`${c.ctype}-${c.id}`} className="grid grid-cols-[36px_1fr_90px_90px_100px] gap-3 items-center p-3 border-b border-neutral-800 last:border-0">
              <span className="font-display text-xl text-neutral-500">{i + 1}</span>
              <div className="flex items-center gap-3 min-w-0">
                {c.cover_image && <img src={resolveFileUrl(c.cover_image)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />}
                <div className="min-w-0">
                  <div className="font-headline uppercase truncate">{c.title}</div>
                  <div className="font-mono-label text-[9px] text-neutral-500 truncate">{c.artist}</div>
                </div>
              </div>
              <span className="chip">{c.ctype?.toUpperCase()}</span>
              <span className="font-display text-lg text-right">{Number(c.plays || 0).toLocaleString()}</span>
              <span className="font-display text-lg text-right text-[var(--accent)]">{fmt$(c.revenue)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className={`border ${accent ? "border-[var(--accent)]" : "border-neutral-800"} bg-neutral-950 p-5`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono-label text-[10px] text-neutral-400">{label}</span>
        <Icon size={14} className={accent ? "text-[var(--accent)]" : "text-neutral-500"} />
      </div>
      <div className={`font-display text-3xl ${accent ? "text-[var(--accent)]" : ""}`}>{value}</div>
      {sub && <div className="font-mono-label text-[9px] text-neutral-500 mt-2 truncate">{sub}</div>}
    </div>
  );
}

function EmptyHint({ children }) {
  return <p className="font-mono-label text-[10px] text-neutral-500 border border-dashed border-neutral-800 p-6 text-center">{children}</p>;
}

// ─────────────────────────────────────────────────────────────────────────────
// PAYOUTS PANEL
// ─────────────────────────────────────────────────────────────────────────────
function PayoutsPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState(null);
  const load = () => {
    api.get(`/admin/payouts?status=${status}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (pid, next, note = null) => {
    setBusy(pid);
    try {
      await api.put(`/admin/payouts/${pid}/status`, { status: next, note });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-payouts">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-headline text-2xl uppercase">Artist Payouts</h2>
        <div className="flex gap-1" data-testid="payout-status-filter">
          {["pending", "approved", "paid", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`font-mono-label text-[10px] px-3 py-2 border ${status === s ? "border-[var(--accent)] text-[var(--accent)]" : "border-neutral-800 text-neutral-400 hover:text-white"}`}
              data-testid={`payout-filter-${s}`}
            >
              {s.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyHint>No payouts in this state.</EmptyHint>
      ) : (
        <div className="border border-neutral-800" data-testid="admin-payouts-list">
          <div className="grid grid-cols-[1fr_120px_100px_140px_220px] gap-3 px-3 py-2 border-b border-neutral-800 font-mono-label text-[9px] text-neutral-500 bg-neutral-950">
            <div>ARTIST</div>
            <div className="text-right">AMOUNT</div>
            <div>METHOD</div>
            <div>STATUS</div>
            <div className="text-right">ACTIONS</div>
          </div>
          {rows.map((p) => (
            <div key={p.id} className="grid grid-cols-[1fr_120px_100px_140px_220px] gap-3 items-center p-3 border-b border-neutral-800 last:border-0" data-testid={`admin-payout-${p.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {p.artist?.avatar && <img src={resolveFileUrl(p.artist.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />}
                <div className="min-w-0">
                  <div className="font-headline uppercase truncate">{p.artist?.display_name || "—"}</div>
                  <div className="font-mono-label text-[9px] text-neutral-500 truncate">@{p.artist?.username} · {new Date(p.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="font-display text-xl text-right">${Number(p.amount).toFixed(2)}</div>
              <div className="font-mono-label text-[10px] text-neutral-400 uppercase">{p.method || "—"}</div>
              <div>
                <span className={`chip ${(p.status === "paid" || p.status === "approved") ? "chip-accent" : ""}`}>{(p.status || "pending").toUpperCase()}</span>
              </div>
              <div className="flex gap-1 justify-end flex-wrap">
                {p.status === "pending" && (
                  <>
                    <button onClick={async () => {
                      const ok = await confirmDialog({
                        title: "Mark payout as paid?",
                        message: `Confirm $${Number(p.amount).toFixed(2)} has been sent to ${p.artist?.display_name || "this artist"} via ${(p.method || "Stripe").toUpperCase()}.`,
                        confirmLabel: "Mark Paid",
                        destructive: false,
                      });
                      if (!ok) return;
                      updateStatus(p.id, "paid", "Marked paid by admin");
                    }} disabled={busy === p.id} className="btn-primary py-1 px-3 text-[10px]" data-testid={`payout-mark-paid-${p.id}`}>
                      <Check size={11} /> Mark Paid
                    </button>
                    <button onClick={async () => {
                      const ok = await confirmDialog({
                        title: "Reject payout?",
                        message: `Reject $${Number(p.amount).toFixed(2)} payout for ${p.artist?.display_name || "this artist"}? They will need to request again. We'll notify them by email (mocked).`,
                        confirmLabel: "Reject Payout",
                      });
                      if (!ok) return;
                      updateStatus(p.id, "rejected", "Rejected by admin");
                    }} disabled={busy === p.id} className="btn-ghost py-1 px-3 text-[10px]" data-testid={`payout-reject-${p.id}`}>
                      <Ban size={11} /> Reject
                    </button>
                  </>
                )}
                {p.status === "rejected" && (
                  <button onClick={() => updateStatus(p.id, "pending")} className="btn-ghost py-1 px-3 text-[10px]" data-testid={`payout-restore-${p.id}`}>
                    Restore → Pending
                  </button>
                )}
                {(p.status === "paid" || p.status === "approved") && (
                  <span className="font-mono-label text-[9px] text-neutral-500">Processed {p.processed_at ? new Date(p.processed_at).toLocaleDateString() : ""}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="font-mono-label text-[9px] text-neutral-500 mt-4">
        MOCKED — Marking Paid logs a notification (will email artist once SendGrid key is provided). Money does not move until Stripe Connect is wired.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REPORTS PANEL
// ─────────────────────────────────────────────────────────────────────────────
function ReportsPanel() {
  const [from, setFrom] = useState(() => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildQs = () => {
    const qs = new URLSearchParams();
    if (from) qs.set("from_", new Date(from).toISOString());
    if (to) qs.set("to", new Date(to + "T23:59:59").toISOString());
    return qs.toString();
  };

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/reports?${buildQs()}`);
      setReport(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { run(); }, []); // eslint-disable-line

  const exportCsv = async (kind) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/export?kind=${kind}&${buildQs()}`;
    const token = localStorage.getItem("hc_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${kind}_${from}_to_${to}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="font-mono-label text-[10px] text-neutral-400 block mb-1">FROM</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input-brut" data-testid="report-from" />
        </div>
        <div>
          <label className="font-mono-label text-[10px] text-neutral-400 block mb-1">TO</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input-brut" data-testid="report-to" />
        </div>
        <button onClick={run} disabled={busy} className="btn-primary" data-testid="report-run">{busy ? "Running…" : "Run Report"}</button>
        <div className="flex-1" />
        <button onClick={() => exportCsv("tips")} className="btn-ghost" data-testid="export-tips"><Download size={12} /> Tips CSV</button>
        <button onClick={() => exportCsv("orders")} className="btn-ghost" data-testid="export-orders"><Download size={12} /> Orders CSV</button>
        <button onClick={() => exportCsv("payouts")} className="btn-ghost" data-testid="export-payouts"><Download size={12} /> Payouts CSV</button>
      </div>

      {report && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KPI label="TIPS (RANGE)" value={`$${report.summary.tips_total.toFixed(2)}`} sub={`${report.summary.tip_count} tips`} icon={DollarSign} accent />
            <KPI label="GMV (RANGE)" value={`$${report.summary.gmv.toFixed(2)}`} sub={`${report.summary.order_count} paid orders`} icon={ShoppingBag} />
            <KPI label="TOP ARTISTS" value={report.top_artists.length} sub="ranked by tips" icon={Users} />
            <KPI label="TOP TIPPERS" value={report.top_tippers.length} sub="ranked by amount sent" icon={Crown} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <section>
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TOP ARTISTS (BY TIPS)</div>
              {report.top_artists.length === 0 ? <EmptyHint>No tips in range.</EmptyHint> : (
                <div className="border border-neutral-800" data-testid="report-top-artists">
                  {report.top_artists.map((a, i) => (
                    <div key={a.username} className="flex items-center gap-3 p-3 border-b border-neutral-800 last:border-0">
                      <span className="font-display text-lg text-neutral-500 w-6">{i + 1}</span>
                      <div className="flex-1 font-headline uppercase truncate">{a.display_name}</div>
                      <div className="font-display text-lg text-[var(--accent)]">${a.tips_total.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TOP TIPPERS</div>
              {report.top_tippers.length === 0 ? <EmptyHint>No tippers in range.</EmptyHint> : (
                <div className="border border-neutral-800" data-testid="report-top-tippers">
                  {report.top_tippers.map((u, i) => (
                    <div key={u.username} className="flex items-center gap-3 p-3 border-b border-neutral-800 last:border-0">
                      <span className="font-display text-lg text-neutral-500 w-6">{i + 1}</span>
                      <div className="flex-1 font-headline uppercase truncate">{u.display_name}<span className="text-neutral-500 text-xs ml-2">@{u.username}</span></div>
                      <div className="font-display text-lg text-[var(--accent)]">${u.tipped_total.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="lg:col-span-2">
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TOP CONTENT (ALL-TIME PLAYS)</div>
              <div className="border border-neutral-800" data-testid="report-top-content">
                {report.top_content.map((c, i) => (
                  <div key={`${c.ctype}-${c.id}`} className="grid grid-cols-[28px_1fr_80px_90px_90px] gap-3 items-center p-3 border-b border-neutral-800 last:border-0">
                    <span className="font-display text-lg text-neutral-500">{i + 1}</span>
                    <div className="flex items-center gap-3 min-w-0">
                      {c.cover_image && <img src={resolveFileUrl(c.cover_image)} alt="" className="w-9 h-9 object-cover border border-neutral-800" />}
                      <div className="min-w-0">
                        <div className="font-headline uppercase truncate">{c.title}</div>
                        <div className="font-mono-label text-[9px] text-neutral-500 truncate">{c.artist}</div>
                      </div>
                    </div>
                    <span className="chip">{c.ctype.toUpperCase()}</span>
                    <span className="font-display text-right">{Number(c.plays || 0).toLocaleString()}</span>
                    <span className="font-display text-right text-[var(--accent)]">${c.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBSCRIBERS PANEL (Culture Club newsletter)
// ─────────────────────────────────────────────────────────────────────────────
function SubscribersPanel() {
  const [data, setData] = useState({ subscribers: [], total: 0, active: 0 });
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/subscribers").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id, email) => {
    const ok = await confirmDialog({
      title: "Remove subscriber?",
      message: `${email} will be removed from the Culture Club newsletter list.`,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setBusy(id);
    try { await api.delete(`/admin/subscribers/${id}`); load(); }
    finally { setBusy(null); }
  };

  const exportCsv = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscribers/export`;
    const token = localStorage.getItem("hc_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "culture-club-subscribers.csv";
    document.body.appendChild(link); link.click(); link.remove();
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">CULTURE CLUB</div>
          <h2 className="font-headline text-2xl uppercase">Newsletter Subscribers</h2>
        </div>
        <button onClick={exportCsv} className="btn-ghost" data-testid="subscribers-export"><Download size={12} /> Export CSV</button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <KPI label="ACTIVE" value={data.active} sub="opted in" icon={Mail} accent />
        <KPI label="TOTAL" value={data.total} sub="all-time signups" icon={Users} />
        <KPI label="SENDER" value="SendGrid" sub={process.env.REACT_APP_SENDGRID_STATUS || "Mocked until key drop"} icon={Crown} />
      </div>

      {data.subscribers.length === 0 ? (
        <EmptyHint>No subscribers yet — share <code className="text-[var(--accent)]">/culture-club</code> to start collecting.</EmptyHint>
      ) : (
        <div className="border border-neutral-800" data-testid="subscribers-list">
          <div className="grid grid-cols-[1fr_180px_120px_120px_40px] gap-3 px-3 py-2 border-b border-neutral-800 font-mono-label text-[9px] text-neutral-500 bg-neutral-950">
            <div>EMAIL</div>
            <div>NAME</div>
            <div>SOURCE</div>
            <div>JOINED</div>
            <div></div>
          </div>
          {data.subscribers.map((s) => (
            <div key={s.id} className="grid grid-cols-[1fr_180px_120px_120px_40px] gap-3 items-center px-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`subscriber-row-${s.id}`}>
              <div className="font-mono text-sm text-neutral-200 truncate">{s.email}</div>
              <div className="text-neutral-400 text-sm truncate">{s.name || "—"}</div>
              <div className="font-mono-label text-[10px] text-neutral-500 uppercase">{s.source || "—"}</div>
              <div className="font-mono-label text-[10px] text-neutral-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</div>
              <button onClick={() => remove(s.id, s.email)} disabled={busy === s.id} className="text-neutral-500 hover:text-[var(--accent)]" data-testid={`subscriber-delete-${s.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MODERATION PANEL — verification queue + commission settings
// ─────────────────────────────────────────────────────────────────────────────
function ModerationPanel() {
  const [queue, setQueue] = useState([]);
  const [settings, setSettings] = useState(null);
  const [feePctInput, setFeePctInput] = useState("");
  const [busy, setBusy] = useState(null);
  const [savingFee, setSavingFee] = useState(false);
  const [shipInput, setShipInput] = useState({ base: "", perItem: "", threshold: "", enabled: true });
  const [savingShip, setSavingShip] = useState(false);
  const [taxInput, setTaxInput] = useState({ rate: "", shipping: true, label: "SALES TAX" });
  const [savingTax, setSavingTax] = useState(false);
  const [marqueeInput, setMarqueeInput] = useState({ text: "", speed: "60", enabled: true });
  const [savingMarquee, setSavingMarquee] = useState(false);

  const load = () => {
    api.get("/admin/verification-queue").then((r) => setQueue(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setFeePctInput(String(r.data?.platform_fee_pct ?? 15));
      setShipInput({
        base: String(r.data?.shipping_base_rate ?? 6.99),
        perItem: String(r.data?.shipping_per_item ?? 1.5),
        threshold: String(r.data?.shipping_free_threshold ?? 75),
        enabled: r.data?.shipping_enabled !== false,
      });
      setTaxInput({
        rate: String(r.data?.tax_rate_pct ?? 0),
        shipping: r.data?.tax_shipping !== false,
        label: r.data?.tax_label || "SALES TAX",
      });
      setMarqueeInput({
        text: r.data?.marquee_text || "",
        speed: String(r.data?.marquee_speed ?? 60),
        enabled: r.data?.marquee_enabled !== false,
      });
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const decide = async (uid, action, username) => {
    const ok = await confirmDialog({
      title: action === "approve" ? "Verify this account?" : "Decline verification?",
      message: action === "approve"
        ? `${username} will receive the verified badge across Culture Connect and their artist profile.`
        : `${username}'s verification request will be cleared without granting the badge. They can re-request later.`,
      confirmLabel: action === "approve" ? "Verify" : "Decline",
      destructive: action !== "approve",
    });
    if (!ok) return;
    setBusy(uid);
    try {
      if (action === "approve") {
        await api.put(`/admin/users/${uid}/verify?verified=true`);
      } else {
        await api.put(`/admin/users/${uid}/verify-decline`);
      }
      load();
    } finally { setBusy(null); }
  };

  const saveFee = async () => {
    const v = parseFloat(feePctInput);
    if (isNaN(v) || v < 0 || v > 50) {
      toast.error("Platform fee must be between 0 and 50%");
      return;
    }
    setSavingFee(true);
    try {
      const r = await api.put("/admin/settings", { platform_fee_pct: v });
      setSettings(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSavingFee(false); }
  };

  const saveShipping = async () => {
    const base = parseFloat(shipInput.base);
    const perItem = parseFloat(shipInput.perItem);
    const threshold = parseFloat(shipInput.threshold);
    if ([base, perItem, threshold].some((v) => isNaN(v) || v < 0)) {
      toast.error("Shipping values must be non-negative numbers.");
      return;
    }
    setSavingShip(true);
    try {
      const r = await api.put("/admin/settings", {
        shipping_enabled: shipInput.enabled,
        shipping_base_rate: base,
        shipping_per_item: perItem,
        shipping_free_threshold: threshold,
      });
      setSettings(r.data);
      toast.success("Shipping rates saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save shipping");
    } finally { setSavingShip(false); }
  };

  const saveTax = async () => {
    const rate = parseFloat(taxInput.rate);
    if (isNaN(rate) || rate < 0 || rate > 30) {
      toast.error("Tax rate must be between 0 and 30%.");
      return;
    }
    setSavingTax(true);
    try {
      const r = await api.put("/admin/settings", {
        tax_rate_pct: rate,
        tax_shipping: taxInput.shipping,
        tax_label: taxInput.label,
      });
      setSettings(r.data);
      toast.success("Tax settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save tax settings");
    } finally { setSavingTax(false); }
  };

  const saveMarquee = async () => {
    const speed = parseInt(marqueeInput.speed, 10);
    if (isNaN(speed) || speed < 10 || speed > 300) {
      toast.error("Ticker speed must be between 10 and 300.");
      return;
    }
    setSavingMarquee(true);
    try {
      const r = await api.put("/admin/settings", {
        marquee_text: marqueeInput.text,
        marquee_speed: speed,
        marquee_enabled: marqueeInput.enabled,
      });
      setSettings(r.data);
      toast.success("Ticker saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ticker");
    } finally { setSavingMarquee(false); }
  };

  return (
    <div className="space-y-10" data-testid="admin-moderation">
      <section data-testid="moderation-commission">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">PLATFORM COMMISSION · BOOKINGS</div>
        <h2 className="font-headline text-2xl uppercase mb-6">Booking Fee Split</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-5">
          <div className="border border-[var(--accent)] bg-[#1a0a05] p-5">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">PLATFORM TAKES</div>
            <div className="font-display text-5xl text-[var(--accent)]" data-testid="fee-platform">{Number(settings?.platform_fee_pct ?? 15).toFixed(0)}%</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">CREATOR KEEPS</div>
            <div className="font-display text-5xl" data-testid="fee-creator">{(100 - Number(settings?.platform_fee_pct ?? 15)).toFixed(0)}%</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">ON A $1,000 GIG</div>
            <div className="font-display text-3xl text-[var(--accent)]">${(1000 * (100 - (settings?.platform_fee_pct ?? 15)) / 100).toFixed(0)}</div>
            <div className="font-mono-label text-[9px] text-neutral-500 mt-2">CREATOR PAYOUT (NET ${ (1000 * (settings?.platform_fee_pct ?? 15) / 100).toFixed(0) } TO PLATFORM)</div>
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-xs">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">PLATFORM FEE (%)</div>
            <input type="number" step="0.5" min="0" max="50" className="input-brut" value={feePctInput} onChange={(e) => setFeePctInput(e.target.value)} data-testid="fee-input" />
          </div>
          <button onClick={saveFee} disabled={savingFee} className="btn-primary" data-testid="fee-save">{savingFee ? "Saving…" : "Save Commission"}</button>
        </div>
        <p className="font-mono-label text-[9px] text-neutral-500 mt-3">APPLIES TO ALL NEW BOOKING CHECKOUTS · EXISTING TRANSACTIONS LOCK IN THE RATE AT TIME OF PAYMENT.</p>
      </section>

      <section data-testid="moderation-shipping" className="border-t border-neutral-800 pt-8">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">STOREFRONT · SHIPPING RATES</div>
        <h2 className="font-headline text-2xl uppercase mb-6">Merch Shipping</h2>
        <div className="grid sm:grid-cols-4 gap-4 mb-5">
          <div className="border border-[var(--accent)] bg-[#1a0a05] p-5">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">BASE RATE (1ST ITEM)</div>
            <div className="font-display text-3xl text-[var(--accent)]" data-testid="ship-base">${Number(settings?.shipping_base_rate ?? 6.99).toFixed(2)}</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">PER EXTRA UNIT</div>
            <div className="font-display text-3xl" data-testid="ship-per-item">${Number(settings?.shipping_per_item ?? 1.5).toFixed(2)}</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">FREE SHIPPING ABOVE</div>
            <div className="font-display text-3xl" data-testid="ship-threshold">${Number(settings?.shipping_free_threshold ?? 75).toFixed(0)}</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">STATUS</div>
            <div className={`font-display text-2xl ${settings?.shipping_enabled === false ? "text-neutral-500" : "text-[var(--accent)]"}`} data-testid="ship-status">
              {settings?.shipping_enabled === false ? "OFF" : "LIVE"}
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">BASE RATE ($)</div>
            <input type="number" step="0.01" min="0" className="input-brut" value={shipInput.base} onChange={(e) => setShipInput({ ...shipInput, base: e.target.value })} data-testid="ship-base-input" />
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">PER EXTRA UNIT ($)</div>
            <input type="number" step="0.01" min="0" className="input-brut" value={shipInput.perItem} onChange={(e) => setShipInput({ ...shipInput, perItem: e.target.value })} data-testid="ship-per-item-input" />
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">FREE-SHIP THRESHOLD ($) · 0 to disable</div>
            <input type="number" step="1" min="0" className="input-brut" value={shipInput.threshold} onChange={(e) => setShipInput({ ...shipInput, threshold: e.target.value })} data-testid="ship-threshold-input" />
          </div>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={shipInput.enabled} onChange={(e) => setShipInput({ ...shipInput, enabled: e.target.checked })} data-testid="ship-enabled" />
              CHARGE SHIPPING
            </label>
            <button onClick={saveShipping} disabled={savingShip} className="btn-primary" data-testid="ship-save">{savingShip ? "Saving…" : "Save Shipping"}</button>
          </div>
        </div>
        <p className="font-mono-label text-[9px] text-neutral-500 mt-3">
          FLAT-RATE MODEL · SHIPPING = BASE + (UNITS − 1) × PER-EXTRA · TOGGLES TO FREE WHEN CART SUBTOTAL ≥ THRESHOLD.
        </p>
      </section>

      <section data-testid="moderation-tax" className="border-t border-neutral-800 pt-8">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">STOREFRONT · SALES TAX</div>
        <h2 className="font-headline text-2xl uppercase mb-6">Checkout Tax</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-5">
          <div className="border border-[var(--accent)] bg-[#1a0a05] p-5">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">CURRENT RATE</div>
            <div className="font-display text-3xl text-[var(--accent)]" data-testid="tax-rate">{Number(settings?.tax_rate_pct ?? 0).toFixed(2)}%</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">LABEL SHOWN TO BUYERS</div>
            <div className="font-display text-2xl" data-testid="tax-label">{settings?.tax_label || "SALES TAX"}</div>
          </div>
          <div className="border border-neutral-800 p-5">
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">TAX BASE</div>
            <div className="font-display text-2xl" data-testid="tax-shipping-mode">
              {settings?.tax_shipping !== false ? "ITEMS + SHIPPING" : "ITEMS ONLY"}
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">RATE (%) · 0 to disable</div>
            <input type="number" step="0.01" min="0" max="30" className="input-brut" value={taxInput.rate} onChange={(e) => setTaxInput({ ...taxInput, rate: e.target.value })} data-testid="tax-rate-input" />
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">LABEL (SALES TAX / VAT / GST…)</div>
            <input type="text" maxLength={40} className="input-brut" value={taxInput.label} onChange={(e) => setTaxInput({ ...taxInput, label: e.target.value })} data-testid="tax-label-input" />
          </div>
          <label className="flex items-center gap-2 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
            <input type="checkbox" checked={taxInput.shipping} onChange={(e) => setTaxInput({ ...taxInput, shipping: e.target.checked })} data-testid="tax-shipping-toggle" />
            APPLY TAX TO SHIPPING
          </label>
          <button onClick={saveTax} disabled={savingTax} className="btn-primary" data-testid="tax-save">{savingTax ? "Saving…" : "Save Tax"}</button>
        </div>
        <p className="font-mono-label text-[9px] text-neutral-500 mt-3">
          TAX = (SUBTOTAL − DISCOUNT{" "}{settings?.tax_shipping !== false ? "+ SHIPPING" : ""}) × RATE · APPLIES ONLY TO MERCH + BOOK ORDERS · BOOKINGS & TIPS ARE NOT TAXED HERE (STRIPE CONNECT HANDLES CREATOR-SIDE TAX WHEN LIVE).
        </p>
      </section>

      <section data-testid="moderation-marquee">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">HOMEPAGE TICKER · MOVING BANNER</div>
        <h2 className="font-headline text-2xl uppercase mb-3">Home Marquee</h2>
        <p className="font-mono-label text-[10px] text-neutral-500 mb-5">
          Scrolls above the featured mixtape on the home page. Use " · " to separate items. Toggle off to hide the ticker entirely.
        </p>
        <div className="grid gap-4">
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">TICKER TEXT · UP TO 500 CHARACTERS</div>
            <textarea
              rows={3}
              maxLength={500}
              className="input-brut resize-none"
              placeholder="NOW SPINNING · NEW DROP · TIP THE ARTIST · …"
              value={marqueeInput.text}
              onChange={(e) => setMarqueeInput({ ...marqueeInput, text: e.target.value })}
              data-testid="marquee-text-input"
            />
            <div className="font-mono-label text-[9px] text-neutral-500 mt-1 text-right">
              {(marqueeInput.text || "").length}/500
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div>
              <div className="font-mono-label text-[10px] text-neutral-400 mb-1">SPEED · 10 (SLOW) — 300 (FAST)</div>
              <input
                type="number"
                min="10"
                max="300"
                step="5"
                className="input-brut"
                value={marqueeInput.speed}
                onChange={(e) => setMarqueeInput({ ...marqueeInput, speed: e.target.value })}
                data-testid="marquee-speed-input"
              />
            </div>
            <label className="flex items-center gap-2 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
              <input
                type="checkbox"
                checked={marqueeInput.enabled}
                onChange={(e) => setMarqueeInput({ ...marqueeInput, enabled: e.target.checked })}
                data-testid="marquee-enabled-toggle"
              />
              SHOW TICKER ON HOMEPAGE
            </label>
            <button
              onClick={saveMarquee}
              disabled={savingMarquee}
              className="btn-primary"
              data-testid="marquee-save"
            >
              {savingMarquee ? "Saving…" : "Save Ticker"}
            </button>
          </div>
          {/* Live preview */}
          {marqueeInput.enabled && (marqueeInput.text || "").trim() && (
            <div className="border border-neutral-800" data-testid="marquee-preview">
              <div className="bg-[var(--accent)] text-black overflow-hidden">
                <div
                  className="font-mono-label text-[11px] py-3 px-6 whitespace-nowrap"
                  style={{
                    animation: `hcTickerScroll ${Math.max(8, 60 / (parseInt(marqueeInput.speed, 10) / 60 || 1))}s linear infinite`,
                    display: "inline-block",
                  }}
                >
                  {marqueeInput.text}
                </div>
              </div>
              <div className="font-mono-label text-[9px] text-neutral-500 px-4 py-2 border-t border-neutral-800">
                LIVE PREVIEW
              </div>
            </div>
          )}
        </div>
      </section>

      <section data-testid="moderation-queue">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">VERIFICATION QUEUE</div>
        <h2 className="font-headline text-2xl uppercase mb-6">Pending Verifications · {queue.length}</h2>
        {queue.length === 0 ? (
          <EmptyHint>No verification requests waiting. Creators can request a badge from their profile.</EmptyHint>
        ) : (
          <div className="border border-neutral-800" data-testid="verification-list">
            <div className="grid grid-cols-[1fr_140px_140px_180px] gap-3 px-3 py-2 border-b border-neutral-800 font-mono-label text-[9px] text-neutral-500 bg-neutral-950">
              <div>USER</div>
              <div>TYPE</div>
              <div>REQUESTED</div>
              <div className="text-right">ACTIONS</div>
            </div>
            {queue.map((u) => (
              <div key={u.id} className="grid grid-cols-[1fr_140px_140px_180px] gap-3 items-center px-3 py-3 border-b border-neutral-800 last:border-0" data-testid={`verify-row-${u.username}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {u.avatar ? (
                    <img src={resolveFileUrl(u.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />
                  ) : (
                    <div className="w-10 h-10 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-display text-sm">{(u.display_name || "?").slice(0, 2).toUpperCase()}</div>
                  )}
                  <div className="min-w-0">
                    <div className="font-headline uppercase truncate">{u.display_name}</div>
                    <div className="font-mono-label text-[9px] text-neutral-500 truncate">@{u.username} · {u.service_title || u.company_name || u.email}</div>
                  </div>
                </div>
                <div className="font-mono-label text-[10px] text-neutral-400 uppercase">{u.account_type || (u.is_artist ? "artist" : "fan")}</div>
                <div className="font-mono-label text-[10px] text-neutral-500">{u.verification_requested_at ? new Date(u.verification_requested_at).toLocaleDateString() : "—"}</div>
                <div className="flex gap-1 justify-end flex-wrap">
                  <button onClick={() => decide(u.id, "approve", u.username)} disabled={busy === u.id} className="btn-primary py-1 px-3 text-[10px]" data-testid={`verify-approve-${u.username}`}>
                    <Check size={11} /> Verify
                  </button>
                  <button onClick={() => decide(u.id, "decline", u.username)} disabled={busy === u.id} className="btn-ghost py-1 px-3 text-[10px]" data-testid={`verify-decline-${u.username}`}>
                    <Ban size={11} /> Decline
                  </button>
                  <a href={`/profile/${u.username}`} target="_blank" rel="noreferrer" className="btn-ghost py-1 px-3 text-[10px]" data-testid={`verify-view-${u.username}`}>View</a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

